import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { requestWithHeaders } from "../beta/utils/fetchData";
import LmLoader from "./Lm-Loader";
import Loading from "../beta/components/helpers/Loading";
import { checkForXSS, safeRedirect } from "../beta/utils/queryStringHelper";

const ResetPassword = () => {
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [anyDirty, setAnyDirty] = useState(false);
    const [errors, setErrors] = useState([]);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [antiforgeryToken, setAntiforgeryToken] = useState("");
    const [success, setSuccess] = useState(false);
    const [validationRules, setValidationRules] = useState({});
    const [validationResults, setValidationResults] = useState({});
    const [userId, setUserId] = useState("");
    const [code, setCode] = useState("");
    const [passwordExpired, setPasswordExpired] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(false);

    useEffect(() => {
        setIsCheckingAuth(true);

        const tokenInput = document.querySelector(
            'input[name="__RequestVerificationToken"]'
        );
        setAntiforgeryToken(tokenInput ? tokenInput.value : "");

        fetch("/api/authentication/refresh-token", {
            method: "POST",
            credentials: "include",
        })
            .then((response) => response.json())
            .then((data) => {
                if (data?.accessToken) {
                    const redirectUrl = checkForXSS(
                        new URLSearchParams(window.location.search).get(
                            "ReturnUrl"
                        ) || "/"
                    );
                    safeRedirect(redirectUrl);
                } else {
                    setIsCheckingAuth(false);
                }
            })
            .catch(() => {
                setIsCheckingAuth(false);
            });

        const userIdInput = document.getElementById("userId");
        const codeInput = document.getElementById("code");
        const passwordExpiredInput = document.getElementById("passwordExpired");
        let userIdValue = userIdInput ? userIdInput.value : "";
        let codeValue = codeInput ? codeInput.value : "";
        let passwordExpiredValue = passwordExpiredInput
            ? passwordExpiredInput.value
            : "";

        const params = new URLSearchParams(window.location.search);

        if (!userIdValue || !codeValue) {
            if (!userIdValue) userIdValue = params.get("userId") || "";
            if (!codeValue) codeValue = params.get("code") || "";
        }

        if (!passwordExpiredValue) {
            passwordExpiredValue = params.get("passwordExpired") || "";
        }

        setUserId(userIdValue);
        setCode(codeValue);
        setPasswordExpired(
            passwordExpiredValue === "true" || passwordExpiredValue === "1"
        );
    }, []);

    useEffect(() => {
        setValidationRules({
            minLength: {
                displayText: "Minimum 8 characters",
                validate: (value) => /(^\S{8,})/.test(value),
            },
            uppercase: {
                displayText: "1 uppercase character",
                validate: (value) => /([A-Z])+/.test(value),
            },
            lowercase: {
                displayText: "1 lowercase character",
                validate: (value) => /([a-z])+/.test(value),
            },
            special: {
                displayText: "1 special character",
                validate: (value) =>
                    /([@%\+\\/'!#\$\^\?:,\(\)\{\}\[\]\~\-_.])+/.test(value),
            },
            numeric: {
                displayText: "1 numeric character (0-9)",
                validate: (value) => /([0-9])+/.test(value),
            },
            noDisallowed: {
                displayText: "No disallowed characters",
                validate: (value) => {
                    if (value && value.length > 0) {
                        return !/^((\S)*([^A-Za-z0-9@%\+\\/'!#\$\^\?:,\(\)\{\}\[\]\~\-_.])(\S)*)+/.test(
                            value
                        );
                    }
                },
            },
        });
    }, []);

    useEffect(() => {
        const results = {};
        Object.keys(validationRules).forEach((rule) => {
            results[rule] = validationRules[rule].validate(password);
        });
        setValidationResults(results);
    }, [password, validationRules]);

    const doesFieldPassRule = (rule) => {
        return validationResults[rule];
    };

    const isPasswordValid = () => {
        return Object.values(validationResults).every(Boolean);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors([]);
        if (!password || !isPasswordValid()) {
            return;
        }
        setRequestInProgress(true);

        const formData = {
            UserId: userId,
            Code: code,
            Password: password,
        };

        const submitResetPasswordRequest = async () => {
            try {
                const result = await requestWithHeaders(
                    "/api/account/reset-password",
                    "POST",
                    formData,
                    {
                        "X-XSRF-TOKEN": antiforgeryToken,
                    }
                );
                if (result.response && result.response.ok) {
                    setSuccess(true);
                } else {
                    const data =
                        (result && result.error && result.error.data) || {};
                    setErrors(data.errors || ["Password reset failed."]);
                }
            } catch (error) {
                setErrors(["Password reset failed."]);
                console.error("Error resetting password:", error);
            } finally {
                setRequestInProgress(false);
            }
        };

        submitResetPasswordRequest();
    };

    if (isCheckingAuth) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Loading enabled={true} spinnerText="Loading..." />
            </div>
        );
    }

    return (
        <div className="login-page-wrapper">
            <div id="reset-password-block" className="login-block-container">
                {!success && errors.length === 0 && (
                    <>
                        <div className="login-block-header">
                            <img
                                className="logo"
                                src="/images/logo.svg"
                                alt="LiquidMetrix Logo"
                            />
                            <ul id="validationList">
                                {Object.keys(validationRules).map((rule) => (
                                    <li
                                        key={rule}
                                        className={
                                            doesFieldPassRule(rule)
                                                ? "validation-rule success"
                                                : "validation-rule"
                                        }
                                    >
                                        <span
                                            className={
                                                doesFieldPassRule(rule)
                                                    ? "glyphicon glyphicon-ok"
                                                    : "glyphicon glyphicon-remove"
                                            }
                                        ></span>
                                        {validationRules[rule].displayText}
                                    </li>
                                ))}
                                <div className="clearfix"></div>
                            </ul>
                            <div className="description">
                                {passwordExpired && (
                                    <span>Your password has expired.</span>
                                )}
                                <span>
                                    Please enter your new password below.
                                </span>
                            </div>
                        </div>

                        <div className="login-block-body">
                            <form
                                method="post"
                                autoComplete="off"
                                noValidate
                                onSubmit={handleSubmit}
                            >
                                <div
                                    className="lm-input-group"
                                    style={{ position: "relative" }}
                                >
                                    <span
                                        className="input-group-addon visibility-toggle"
                                        onClick={() =>
                                            setShowPassword((v) => !v)
                                        }
                                    >
                                        <span
                                            className={`glyphicon ${
                                                showPassword
                                                    ? "glyphicon-eye-open"
                                                    : "glyphicon-eye-close"
                                            }`}
                                        ></span>
                                    </span>
                                    <input
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        name="password"
                                        required
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            setAnyDirty(true);
                                        }}
                                        className={
                                            anyDirty && !password
                                                ? "has-error"
                                                : ""
                                        }
                                        disabled={requestInProgress}
                                        style={{ paddingRight: "40px" }}
                                    />
                                    <span className="highlight"></span>
                                    <span className="bar"></span>
                                    <label htmlFor="password">Password</label>
                                    <span className="error">
                                        {anyDirty &&
                                            !password &&
                                            "The password field is required."}
                                    </span>
                                </div>
                                <button
                                    className={
                                        "lm-btn primary sign-in-btn" +
                                        (requestInProgress ? " loading" : "")
                                    }
                                    type="submit"
                                    disabled={
                                        !isPasswordValid() || requestInProgress
                                    }
                                >
                                    <span className="btn-label">
                                        Reset Password
                                    </span>
                                    <LmLoader />
                                </button>
                            </form>
                        </div>

                        <div className="login-block-footer">
                            <Link to="/Account/Login" className="lm-link">
                                <span>Back to Sign In</span>
                            </Link>
                        </div>
                    </>
                )}

                {(success || errors.length > 0) && (
                    <>
                        <div className="login-block-header">
                            <img
                                className="logo"
                                src="/images/logo.svg"
                                alt="LiquidMetrix Logo"
                            />
                            <div className="errors">
                                {errors.length > 0 && anyDirty && (
                                    <p>
                                        {errors.map((err, i) => (
                                            <span key={i}>{err}</span>
                                        ))}
                                    </p>
                                )}
                            </div>
                            <div className="description">
                                {errors.length === 0 && (
                                    <span>Your password has been changed.</span>
                                )}
                            </div>
                        </div>

                        <div className="login-block-footer">
                            <Link to="/Account/Login" className="lm-link">
                                <span>Back to Sign In</span>
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
